import React, { useState, useEffect, useRef, useCallback } from 'react';
import HeroSection from './HeroSection';
import Navigation from './Navigation';
import AboutSection from './AboutSection';
import ExperienceSection from './ExperienceSection';
import SkillsSection from './SkillsSection';
import EducationSection from './EducationSection';
import ContactSection from './ContactSection';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';
import { SectionType } from '../types';
import { useScrollPosition } from '../hooks/useScrollPosition';
import { useScrollSpy } from '../hooks/useScrollSpy';

const SECTION_IDS: SectionType[] = [
  'about',
  'experience',
  'skills',
  'education',
  'contact'
];

const Portfolio = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lockedId, setLockedId] = useState<SectionType | null>(null);
  const lockTimer = useRef<ReturnType<typeof setTimeout>>();

  const scrollY = useScrollPosition();
  const isScrolled = scrollY > 10;
  const activeSection = useScrollSpy(SECTION_IDS, scrollY, lockedId) as SectionType;

  // Navigation is plain anchors, so the browser handles smooth scrolling,
  // the hash and history. We only pin the indicator while the scroll runs.
  const handleNavClick = useCallback((section: SectionType) => {
    setMobileMenuOpen(false);
    setLockedId(section);

    clearTimeout(lockTimer.current);
    lockTimer.current = setTimeout(() => setLockedId(null), 700);
  }, []);

  useEffect(() => () => clearTimeout(lockTimer.current), []);

  const sections: { id: SectionType; content: JSX.Element }[] = [
    { id: 'about', content: <AboutSection /> },
    { id: 'experience', content: <ExperienceSection /> },
    { id: 'skills', content: <SkillsSection /> },
    { id: 'education', content: <EducationSection /> },
    { id: 'contact', content: <ContactSection /> }
  ];

  return (
    <div className="min-h-screen bg-dark-bg text-dark-text font-sans text-left">
      <a
        href="#about"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:rounded-lg focus:bg-dark-surface focus:text-dark-heading focus:border focus:border-accent"
      >
        Skip to content
      </a>

      <HeroSection />

      <Navigation
        activeSection={activeSection}
        isScrolled={isScrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        handleNavClick={handleNavClick}
      />

      <main className="max-w-5xl mx-auto px-5">
        {sections.map(({ id, content }, index) => (
          <section
            key={id}
            id={id}
            className={`scroll-mt-24 py-14 md:py-20 ${
              index > 0 ? 'border-t border-dark-border/60' : ''
            }`}
          >
            {content}
          </section>
        ))}
      </main>

      <Footer />
      <ScrollToTop scrollY={scrollY} />
    </div>
  );
};

export default Portfolio;
